import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Snackbar, Slide } from '@mui/material';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import {
    Dashboard as DashboardIcon,
    Folder as FolderIcon,
    Monitor as MonitorIcon,
    Speed as SpeedIcon,
    LocationOn,
    Settings,
    ElectricBolt,
    Build,
    AttachMoney,
    Assessment,
    Analytics,
    ArrowBack,
    Business as CompanyIcon,
    Groups as TeamIcon,
    CreditCard as BillingIcon,
    Construction as EquipmentIcon,
    ShowChart as LoadProfileIcon,
    AccountBalance as BudgetIcon,
    Domain as AllCompaniesIcon,
    People as AllUsersIcon,
    Memory as SolverIcon,
    Bolt as DerChargesIcon,
    BugReport as BugIcon,
    Visibility as ActivityIcon,
    AdminPanelSettings as AdminIcon,
    Sensors as SensorsIcon,
    AcUnit as ColdStorageIcon,
    EvStation as EvStationIcon,
    Science as ScienceIcon,
    MenuBook as WikiIcon,
    LocalShipping as FleetIcon,
    Schedule as ScheduleIcon,
    ElectricalServices as AcDcIcon,
    Handshake as HandshakeIcon,
    Tune as TuneIcon,
} from '@mui/icons-material';

const APP_NAV_ITEMS = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, color: vortxColors.gold, requiresProduct: null },
    { id: 'all-projects', label: 'All Projects', path: '/all-projects', icon: FolderIcon, color: vortxColors.gold, requiresProduct: null },
    { id: 'site-dna', label: 'Site DNA', path: '/projects', icon: FolderIcon, color: vortxColors.gold, requiresProduct: null },
    { id: 'flux-ev', label: 'Flux EV', path: '/ev', icon: EvStationIcon, color: '#4CAF50', requiresProduct: null },
    { id: 'cold-storage', label: 'Flux COLD', path: '/cold-storage', icon: ColdStorageIcon, color: '#2196F3', requiresProduct: null },
    { id: 'flux-ems', label: 'Flux EMS', path: '/ems', icon: MonitorIcon, color: vortxColors.teal, requiresProduct: 'flux_ems' },
    { id: 'flux-phd', label: 'Flux PhD', path: '/phd', icon: SpeedIcon, color: '#9b59b6', requiresProduct: 'flux_phd' },
    { id: 'partner-portal', label: 'Partner Portal', path: '/partner', icon: HandshakeIcon, color: '#FF9800', requiresProduct: 'partner_portal' },
];

const SCENARIO_TAB_ITEMS = [
    { index: 0, label: 'Site', icon: LocationOn },
    { index: 1, label: 'Utility', icon: Settings },
    { index: 2, label: 'Load', icon: ElectricBolt },
    { index: 3, label: 'Design', icon: Build },
    { index: 4, label: 'Budget/Terms', icon: AttachMoney },
    { index: 5, label: 'Variants', icon: Assessment },
    { index: 6, label: 'Results', icon: Analytics },
];

// Flux EMS navigation items
const FLUX_EMS_NAV_ITEMS = [
    { id: 'live', label: 'Live', path: '/ems/live', icon: SensorsIcon },
];

// Flux EV scenario-level tabs (inside a scenario editor)
const FLUX_EV_SCENARIO_TAB_ITEMS = [
    { index: 0, label: 'Fleet', icon: FleetIcon },
    { index: 1, label: 'Chargers', icon: EvStationIcon },
    { index: 2, label: 'Utility', icon: ElectricBolt },
    { index: 3, label: 'Schedule', icon: ScheduleIcon },
    { index: 4, label: 'Results', icon: Analytics },
    { index: 5, label: 'Financial', icon: AttachMoney },
];

// Flux PhD navigation items (project-independent)
const FLUX_PHD_NAV_ITEMS = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { id: 'forecasting', label: 'Forecasting', icon: ScienceIcon },
    { id: 'research', label: 'Research', icon: Analytics },
    { id: 'calibration', label: 'Site Calibration', icon: TuneIcon },
    { id: 'wiki', label: 'Wiki', icon: WikiIcon },
];

// Admin navigation items
const ADMIN_ACCOUNT_ITEMS = [
    { id: 'admin-dashboard', label: 'Dashboard', path: '/admin', icon: DashboardIcon },
    { id: 'admin-activity', label: 'Activity Monitor', path: '/admin/activity', icon: ActivityIcon },
    { id: 'admin-company', label: 'Company', path: '/admin/company', icon: CompanyIcon },
    { id: 'admin-team', label: 'Team & Seats', path: '/admin/team', icon: TeamIcon },
    { id: 'admin-billing', label: 'Billing', path: '/admin/billing', icon: BillingIcon },
];

const ADMIN_PRODUCT_ITEMS = [
    { id: 'admin-tariffs', label: 'Tariffs', path: '/admin/tariffs', icon: ElectricBolt },
    { id: 'admin-capex', label: 'CapEx & OpEx', path: '/admin/capex', icon: AttachMoney },
    { id: 'admin-equipment', label: 'Equipment', path: '/admin/equipment', icon: EquipmentIcon },
    { id: 'admin-load-profiles', label: 'Load Profiles', path: '/admin/load-profiles', icon: LoadProfileIcon },
    { id: 'admin-budgets', label: 'Budgets', path: '/admin/budgets', icon: BudgetIcon },
];

const ADMIN_PLATFORM_ITEMS = [
    { id: 'admin-companies', label: 'All Companies', path: '/admin/companies', icon: AllCompaniesIcon },
    { id: 'admin-users', label: 'All Users', path: '/admin/users', icon: AllUsersIcon },
    { id: 'admin-solver', label: 'Solver Monitor', path: '/admin/solver', icon: SolverIcon },
    { id: 'admin-der-charges', label: 'CA DER Charges', path: '/admin/der-charges', icon: DerChargesIcon },
    { id: 'admin-bugs', label: 'Bug Reports', path: '/admin/bugs', icon: BugIcon },
];

const NavSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography sx={{
            fontFamily: vortxTypography.body,
            fontSize: '0.65rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: vortxColors.charcoal,
            opacity: 0.5,
            px: 1,
            mb: 1,
        }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const NavItem = ({ icon: Icon, label, isActive, onClick, color = vortxColors.gold, href }) => {
    const handleClick = (e) => {
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let browser handle it
        }
        e.preventDefault();
        onClick?.();
    };

    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '0.6rem 1rem',
                cursor: 'pointer',
                borderRadius: '8px',
                background: isActive ? `${color}15` : 'transparent',
                borderLeft: '3px solid',
                borderLeftColor: isActive ? color : 'transparent',
                transition: 'all 0.15s ease',
                textDecoration: 'none',
                '&:hover': {
                    background: `${color}10`,
                    borderLeftColor: `${color}60`,
                },
            }}
        >
            <Box sx={{
                color: isActive ? color : vortxColors.charcoal,
                opacity: isActive ? 1 : 0.7,
                transition: 'all 0.15s ease',
            }}>
                {React.isValidElement(Icon) ? Icon : <Icon sx={{ fontSize: 18 }} />}
            </Box>
            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '0.8rem',
                color: isActive ? vortxColors.charcoal : vortxColors.charcoal,
                fontWeight: isActive ? 600 : 400,
                opacity: isActive ? 1 : 0.8,
                transition: 'all 0.15s ease',
            }}>
                {label}
            </Typography>
        </Box>
    );
};

// Back button for navigation
const BackButton = ({ onClick, label = 'Back', href }) => {
    const handleClick = (e) => {
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return;
        }
        e.preventDefault();
        onClick?.();
    };

    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                borderRadius: '8px',
                background: 'transparent',
                transition: 'all 0.15s ease',
                textDecoration: 'none',
                mb: 2,
                '&:hover': {
                    background: `${vortxColors.teal}10`,
                },
            }}
        >
            <ArrowBack sx={{ fontSize: 16, color: vortxColors.teal }} />
            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '0.8rem',
                color: vortxColors.teal,
                fontWeight: 500,
            }}>
                {label}
            </Typography>
        </Box>
    );
};

// Admin Panel Button
const AdminButton = ({ onClick, isActive, href }) => {
    const handleClick = (e) => {
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return;
        }
        e.preventDefault();
        onClick?.();
    };

    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderRadius: '8px',
                background: isActive ? 'rgba(231, 76, 60, 0.1)' : 'rgba(231, 76, 60, 0.05)',
                border: `1px solid ${isActive ? '#e74c3c' : 'rgba(231, 76, 60, 0.2)'}`,
                transition: 'all 0.15s ease',
                textDecoration: 'none',
                '&:hover': {
                    borderColor: '#e74c3c',
                    background: 'rgba(231, 76, 60, 0.1)',
                }
            }}
        >
            <AdminIcon sx={{ fontSize: 18, color: '#e74c3c' }} />
            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#e74c3c',
            }}>
                Admin Panel
            </Typography>
        </Box>
    );
};

const LightNavigation = ({ scenarioTab, onScenarioTabChange, emsSection, onEmsSectionChange, phdSection, onPhdSectionChange, evScenarioTab, onEvScenarioTabChange, isAdmin, isSuperAdmin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile } = useAuth();

    // Access denied notification state
    const [accessDenied, setAccessDenied] = useState(false);
    const [deniedModule, setDeniedModule] = useState('');

    // Check if user has access to a specific product
    const hasProductAccess = (productKey) => {
        if (!productKey) return true;
        return userProfile?.products?.[productKey] === true;
    };

    // Handle navigation with access check
    const handleNavClick = (item) => {
        if (item.requiresProduct && !hasProductAccess(item.requiresProduct)) {
            setDeniedModule(item.label);
            setAccessDenied(true);
            return;
        }
        navigate(item.path);
    };

    // Detect if we're in the scenario editor
    // Detect Site DNA scenario editor (not Flux EV)
    const isInScenarioEditor = location.pathname.startsWith('/projects/') && location.pathname.includes('/scenarios/');

    // Detect Site DNA project-level view (analysis, acdc — not scenario editor)
    const siteDnaProjectMatch = location.pathname.match(/^\/projects\/([^/]+)\/(analysis|acdc)/);
    const isInSiteDnaProject = !!siteDnaProjectMatch;
    const siteDnaProjectId = siteDnaProjectMatch?.[1];

    // Detect if we're in Flux EMS
    const isInFluxEms = location.pathname.startsWith('/ems');

    // Detect if we're in Flux PhD
    const isInFluxPhd = location.pathname.startsWith('/phd');

    // Detect if we're inside a Flux EV scenario editor (/ev/:projectId/scenarios/:scenarioId)
    const isInFluxEvScenario = /^\/ev\/[^/]+\/scenarios\/[^/]+/.test(location.pathname);

    // Detect if we're in admin section
    const isInAdmin = location.pathname.startsWith('/admin');

    // Determine which app nav item is active based on path
    const getActiveAppId = () => {
        const path = location.pathname;
        if (path === '/' || path === '/dashboard') return 'dashboard';
        if (path.startsWith('/projects')) return 'site-dna';
        if (path.startsWith('/ev')) return 'flux-ev';
        if (path.startsWith('/ems') || path.startsWith('/flux')) return 'flux-ems';
        if (path.startsWith('/phd')) return 'flux-phd';
        if (path.startsWith('/partner')) return 'partner-portal';
        if (path.startsWith('/admin')) return 'admin';
        return null;
    };

    const activeAppId = getActiveAppId();

    // Get active admin path for highlighting
    const getActiveAdminPath = () => {
        const path = location.pathname;
        if (path === '/admin') return '/admin';
        const allAdminItems = [...ADMIN_ACCOUNT_ITEMS, ...ADMIN_PRODUCT_ITEMS, ...ADMIN_PLATFORM_ITEMS];
        const match = allAdminItems.find(item => path.startsWith(item.path) && item.path !== '/admin');
        return match?.path || null;
    };

    const activeAdminPath = getActiveAdminPath();

    // Render admin navigation items
    const renderAdminItems = (items, color = '#e74c3c') => (
        items.map((item) => (
            <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                isActive={activeAdminPath === item.path}
                onClick={() => navigate(item.path)}
                color={color}
                href={item.path}
            />
        ))
    );

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Back to Dashboard button when in admin */}
            {isInAdmin && (
                <BackButton
                    onClick={() => navigate('/dashboard')}
                    label="Back to Dashboard"
                    href="/dashboard"
                />
            )}

            {/* App Navigation - always show unless in admin */}
            {!isInAdmin && (
                <NavSection title="Applications">
                    {APP_NAV_ITEMS
                        .filter((item) => !item.requiresProduct || hasProductAccess(item.requiresProduct))
                        .map((item) => (
                            <NavItem
                                key={item.id}
                                icon={item.icon}
                                label={item.label}
                                isActive={activeAppId === item.id}
                                onClick={() => handleNavClick(item)}
                                color={item.color}
                                href={item.path}
                            />
                        ))}
                </NavSection>
            )}

            {/* Admin Navigation - show when in admin section */}
            {isInAdmin && isAdmin && (
                <>
                    <NavSection title="Account">
                        {renderAdminItems(ADMIN_ACCOUNT_ITEMS)}
                    </NavSection>

                    <NavSection title="Configuration">
                        {renderAdminItems(ADMIN_PRODUCT_ITEMS)}
                    </NavSection>

                    {isSuperAdmin && (
                        <NavSection title="Platform">
                            {renderAdminItems(ADMIN_PLATFORM_ITEMS)}
                        </NavSection>
                    )}
                </>
            )}

            {/* Site DNA Project Navigation — show when inside a project (not in scenario editor) */}
            {isInSiteDnaProject && siteDnaProjectId && (
                <NavSection title="Site DNA">
                    <NavItem
                        icon={Analytics}
                        label="Analysis"
                        isActive={location.pathname.includes('/analysis')}
                        onClick={() => navigate(`/projects/${siteDnaProjectId}/analysis`)}
                        color={vortxColors.gold}
                        href={`/projects/${siteDnaProjectId}/analysis`}
                    />
                    <NavItem
                        icon={AcDcIcon}
                        label="AC/DC Systems"
                        isActive={location.pathname.includes('/acdc')}
                        onClick={() => navigate(`/projects/${siteDnaProjectId}/acdc`)}
                        color={vortxColors.gold}
                        href={`/projects/${siteDnaProjectId}/acdc`}
                    />
                </NavSection>
            )}

            {/* Scenario Tabs - only show when in ScenarioEditor */}
            {isInScenarioEditor && (
                <NavSection title="Scenario">
                    {SCENARIO_TAB_ITEMS.map((item) => (
                        <NavItem
                            key={item.index}
                            icon={item.icon}
                            label={item.label}
                            isActive={scenarioTab === item.index}
                            onClick={() => onScenarioTabChange?.(item.index)}
                            color={vortxColors.gold}
                            href={`${location.pathname}?tab=${item.index}`}
                        />
                    ))}
                </NavSection>
            )}

            {/* Flux EV Scenario Tabs — show when inside a scenario editor */}
            {isInFluxEvScenario && (() => {
                const evMatch = location.pathname.match(/^\/ev\/([^/]+)/);
                const evProjectId = evMatch ? evMatch[1] : '';
                return (
                    <>
                        <BackButton
                            onClick={() => navigate(`/ev/${evProjectId}/scenarios`)}
                            label="Back to Scenarios"
                            href={`/ev/${evProjectId}/scenarios`}
                        />
                        <NavSection title="Scenario">
                            {FLUX_EV_SCENARIO_TAB_ITEMS.map(item => (
                                <NavItem
                                    key={item.index}
                                    icon={item.icon}
                                    label={item.label}
                                    isActive={evScenarioTab === item.index}
                                    onClick={() => onEvScenarioTabChange?.(item.index)}
                                    color="#4CAF50"
                                    href={`${location.pathname}?tab=${item.index}`}
                                />
                            ))}
                        </NavSection>
                    </>
                );
            })()}

            {/* Flux EMS Navigation - only show when in Flux EMS */}
            {isInFluxEms && (
                <NavSection title="Flux EMS">
                    {FLUX_EMS_NAV_ITEMS.map((item) => (
                        <NavItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            isActive={location.pathname.startsWith(item.path)}
                            onClick={() => navigate(item.path)}
                            color={vortxColors.teal}
                            href={item.path}
                        />
                    ))}
                </NavSection>
            )}

            {/* Flux PhD Navigation - only show when in Flux PhD */}
            {isInFluxPhd && (
                <NavSection title="Flux PhD">
                    {FLUX_PHD_NAV_ITEMS.map((item) => (
                        <NavItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            isActive={phdSection === item.id}
                            onClick={() => {
                                navigate(`/phd/${item.id}`);
                                onPhdSectionChange?.(item.id);
                            }}
                            color="#9b59b6"
                            href={`/phd/${item.id}`}
                        />
                    ))}
                </NavSection>
            )}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Admin Panel Button - show when not in admin and user is admin */}
            {isAdmin && !isInAdmin && (
                <Box sx={{ mt: 'auto', pt: 2 }}>
                    <AdminButton
                        onClick={() => navigate('/admin')}
                        isActive={activeAppId === 'admin'}
                        href="/admin"
                    />
                </Box>
            )}

            {/* Access Denied Notification */}
            <Snackbar
                open={accessDenied}
                autoHideDuration={3000}
                onClose={() => setAccessDenied(false)}
                TransitionComponent={Slide}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box
                    sx={{
                        bgcolor: '#fff',
                        border: '1px solid #e74c3c',
                        borderRadius: '8px',
                        px: 3,
                        py: 2,
                        boxShadow: '0 4px 20px rgba(231, 76, 60, 0.2)',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#e74c3c',
                            mb: 0.5,
                        }}
                    >
                        Access Denied
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: 'rgba(231, 76, 60, 0.7)',
                        }}
                    >
                        {deniedModule} access not granted
                    </Typography>
                </Box>
            </Snackbar>
        </Box>
    );
};

export default LightNavigation;
